import { type Entry } from 'contentful'
import { type ComponentPropsWithRef, type ReactElement } from 'react'

import { type TypeHeaderSkeleton } from '@contentful-types/header'
import { type TypePageEntry } from '@contentful-types/page'

import { usePageHeader } from '@contexts/page-header-context'

import { PageFooter } from '@components/page-footer/page-footer'
import { DefaultPageHeader, PageHeader } from '@components/page-header/page-header'
import { type HeaderType } from '@components/ui-core/header/header'
import { Section } from '@components/ui-core/section/section'
import { Sprites } from '@components/ui-core/sprite/sprites'

interface HeaderComponentProps extends ComponentPropsWithRef<'header'> {
  header: Entry<TypeHeaderSkeleton> | undefined
  headerType?: HeaderType
}

function HeaderComponent({ header, headerType, ...rest }: HeaderComponentProps) {
  if (!headerType) {
    return null
  }

  // If we have CMS data, use the CMS-based PageHeader
  // Otherwise, use the default header components for custom pages
  return header ? (
    <PageHeader header={header} {...rest} />
  ) : (
    <DefaultPageHeader type={headerType} {...rest} />
  )
}

export interface PageLayoutProps extends ComponentPropsWithRef<'div'> {
  headerType?: HeaderType
  withFooter?: boolean
  data?: TypePageEntry
  banner?: ReactElement
}

export function PageLayout({
  children,
  headerType,
  withFooter,
  data,
  banner,
  ...rest
}: PageLayoutProps): ReactElement {
  const { headerRef } = usePageHeader()

  return (
    <>
      <Sprites />
      <div {...rest}>
        {banner && (
          <Section data-banner data-background-color="primary-default" className="pt-2x">
            {banner}
          </Section>
        )}
        <HeaderComponent
          ref={headerRef}
          data-global-header
          header={data?.fields.header}
          headerType={headerType}
        />
        <main>
          {children}
          {withFooter && <PageFooter />}
        </main>
      </div>
    </>
  )
}
